import { AutoAdjustBackwardsLimit } from "../../constants/autoAdjustBackwardsLimit";
import { AutoAdjustDateLevel } from "../../constants/autoAdjustDateLevel";
import compactDisplays from "../../constants/compactDisplay";
import defaultGenericNarrativeLabels from "../../constants/defaultGenericNarrativeLabels";
import itemTypeCategories from "../../constants/itemTypeCategories";
import itemTypeIds from "../../constants/itemTypeIds";
import narrativeNumberingOptions from "../../constants/narrativeNumberingOptions";
import { REFERENCE_RELATES_TO } from "../../constants/references";
import subwayDisplayOptions from "../../constants/subwayDisplayOptions";
import { TimelineZoomLimits } from "../../constants/zoomRange";
import { getGregorianCalendarObject } from "../../modules/definition/calendar/gregorianCalendarRaw";
import arrayToListObjectCamelCase from "../../utils/arrayToListObjectCamelCase";

const calendar = getGregorianCalendarObject();

export default {
  autoAdjustLevel: AutoAdjustDateLevel.AdjustSelf,
  autoAdjustBackwardsLimit: AutoAdjustBackwardsLimit.Today,
  includeNarrative: false,
  types: {
    byId: {
      composition: {
        enabled: true,
        canDelete: true,
        id: "composition",
        label: "Composition",
        pluralLabel: "Compositions",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          REFERENCE_RELATES_TO,
          "movementRef",
          "composerRef",
          "sourceRef",
          "witnessRef",
          "participantRef",
          "seeAlsoRef",
          "locationRef",
          "startLocationRef",
          "endLocationRef",
          "categoryRef",
          "associateRef"
        ],
        properties: [],
        superTypes: ["composition"],
        icon: "music",
        color: "iconGreen",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "COM",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      invention: {
        enabled: true,
        canDelete: true,
        id: "invention",
        label: "Invention",
        pluralLabel: "Inventions",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "participantRef",
          "witnessRef",
          "causesRef",
          "seeAlsoRef",
          "locationRef",
          "startLocationRef",
          "endLocationRef",
          "categoryRef",
          "sourceRef",
          "associateRef",
          REFERENCE_RELATES_TO,
          "movementRef"
        ],
        properties: ["notes"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "invention", "discovery"],
        icon: "bulb",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "INV",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      discovery: {
        enabled: true,
        canDelete: true,
        id: "discovery",
        label: "Discovery",
        pluralLabel: "Discovery",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "participantRef",
          "witnessRef",
          "causesRef",
          "seeAlsoRef",
          "locationRef",
          "startLocationRef",
          "endLocationRef",
          "categoryRef",
          "sourceRef",
          REFERENCE_RELATES_TO,
          "movementRef"
        ],
        properties: ["notes"],
        superTypes: ["invention", itemTypeIds.EVENT_ITEM_TYPE_ID, "discovery"],
        icon: "puzzle",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "DIS",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      movement: {
        enabled: true,
        canDelete: true,
        id: "movement",
        label: "Movement",
        pluralLabel: "Movement",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "categoryRef", "sourceRef", "participantRef", "witnessRef"],
        properties: [],
        superTypes: [],
        icon: "droplet",
        color: "iconOrange",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "MV",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.NARRATIVE_FOLDER_TYPE_ID]: {
        enabled: false,
        canDelete: false,
        id: itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
        label: "Narrative Folder",
        pluralLabel: "Narrative Folders",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: [],
        superTypes: [],
        icon: "folder",
        color: "iconMono",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: true,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NF",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: true,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      literature: {
        enabled: true,
        canDelete: true,
        id: "literature",
        label: "Literature",
        pluralLabel: "Literatures",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          REFERENCE_RELATES_TO,
          "movementRef",
          "authorRef",
          "sourceRef",
          "witnessRef",
          "participantRef",
          "seeAlsoRef",
          "locationRef",
          "startLocationRef",
          "endLocationRef",
          "categoryRef",
          "associateRef"
        ],
        properties: [],
        superTypes: ["literature"],
        icon: "scroll",
        color: "iconRed",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "LIT",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.PERSON_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.PERSON_ITEM_TYPE_ID,
        label: "Person",
        pluralLabel: "People",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Birth",
        endDisplayLabel: "Death",
        anniversaryDisplayLabel: "Birthday",
        extendAnniversaryBeyondEnd: false,
        references: [
          "birthplaceRef",
          REFERENCE_RELATES_TO,
          "categoryRef",
          "sourceRef",
          "familyRef",
          "friendRef",
          "rivalRef",
          "parentRef",
          "grandparentRef",
          "stepParentRef",
          "siblingRef",
          "spouseRef",
          "fianceRef",
          "partnerRef",
          "instigatorRef"
        ],
        properties: ["notes", "shortName", "gender", "occupation", "title"],
        superTypes: [],
        icon: "person",
        color: "iconRed",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "PER",
        compactDisplay: compactDisplays.AVATAR_INITIALS,
        category: itemTypeCategories.PERSON,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.LOCATION_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.LOCATION_ITEM_TYPE_ID,
        label: "Location",
        pluralLabel: "Locations",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO, "categoryRef", "sourceRef"],
        properties: ["notes", "shortName"],
        superTypes: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "map",
        color: "iconOrange",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "LOC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        category: itemTypeCategories.LOCATION,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.MINDMAP_NODE_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.MINDMAP_NODE_TYPE_ID,
        label: "Mindmap Node",
        pluralLabel: "Nodes",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: [],
        icon: "diamond",
        color: "iconMono",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "NO",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      [itemTypeIds.EVENT_ITEM_TYPE_ID]: {
        enabled: true,
        canDelete: true,
        id: itemTypeIds.EVENT_ITEM_TYPE_ID,
        label: "Event",
        pluralLabel: "Events",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          "categoryRef",
          "locationRef",
          "sourceRef",
          "causesRef",
          "seeAlsoRef",
          REFERENCE_RELATES_TO,
          "participantRef",
          "witnessRef",
          "startLocationRef",
          "endLocationRef",
          "movementRef"
        ],
        properties: ["notes"],
        superTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "discovery", "invention", "artwork", "literature", "composition"],
        icon: "diamond",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: true,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "EV",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: true,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      artwork: {
        enabled: true,
        canDelete: true,
        id: "artwork",
        label: "Artwork",
        pluralLabel: "Artworks",
        labelDisplayLabel: "Name",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: [
          REFERENCE_RELATES_TO,
          "movementRef",
          "sculptorRef",
          "painterRef",
          "architectRef",
          "sourceRef",
          "witnessRef",
          "participantRef",
          "seeAlsoRef",
          "locationRef",
          "startLocationRef",
          "endLocationRef",
          "categoryRef",
          "associateRef"
        ],
        properties: [],
        superTypes: ["artwork"],
        icon: "palette",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: true,
        showOnSidebar: false,
        showAsColumn: false,
        identifierPrefix: "ART",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      source: {
        enabled: true,
        canDelete: true,
        id: "source",
        label: "Source",
        pluralLabel: "Sources",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Created",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: ["categoryRef", "associateRef", REFERENCE_RELATES_TO],
        properties: ["notes", "reliability", "sourceType"],
        superTypes: ["source"],
        icon: "book",
        color: "iconYellow",
        defaultItemColor: null,
        canHaveDate: true,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        identifierPrefix: "SRC",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      },
      category: {
        enabled: true,
        canDelete: true,
        id: "category",
        label: "Category",
        pluralLabel: "Categories",
        labelDisplayLabel: "Label",
        startDisplayLabel: "Start",
        endDisplayLabel: "End",
        anniversaryDisplayLabel: "Anniversary",
        extendAnniversaryBeyondEnd: true,
        references: ["sourceRef", "antagonistRef", "bookRef", REFERENCE_RELATES_TO],
        properties: ["notes"],
        superTypes: ["category"],
        icon: "case",
        color: "iconPurple",
        defaultItemColor: null,
        canHaveDate: false,
        canIncludeInNarrative: false,
        showInSequence: false,
        showOnSidebar: true,
        showAsColumn: true,
        columnIsVisible: true,
        identifierPrefix: "CAT",
        compactDisplay: compactDisplays.SHORT_LABEL,
        isNarrativeFolder: false,
        defaultSequenceType: false,
        defaultNarrativeType: false,
        canHaveLocationUri: false
      }
    },
    allIds: [
      itemTypeIds.EVENT_ITEM_TYPE_ID,
      "artwork",
      "composition",
      "literature",
      itemTypeIds.PERSON_ITEM_TYPE_ID,
      itemTypeIds.LOCATION_ITEM_TYPE_ID,
      "movement",
      "category",
      "source",
      "invention",
      "discovery",
      itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
      itemTypeIds.MINDMAP_NODE_TYPE_ID
    ]
  },
  properties: {
    byId: {
      notes: {
        enabled: true,
        canDelete: true,
        id: "notes",
        key: "notes",
        label: "Notes",
        format: "multitext",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      shortName: {
        enabled: true,
        canDelete: true,
        id: "shortName",
        key: "shortName",
        label: "Short Name",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      gender: {
        enabled: true,
        canDelete: true,
        id: "gender",
        key: "gender",
        label: "Gender",
        format: "list",
        allowed: arrayToListObjectCamelCase(["Male", "Female", "Other"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      occupation: {
        enabled: true,
        canDelete: true,
        id: "occupation",
        key: "occupation",
        label: "Occupation",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      title: {
        enabled: true,
        canDelete: true,
        id: "title",
        key: "title",
        label: "Title",
        format: "text",
        allowed: null,
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      reliability: {
        enabled: true,
        canDelete: true,
        id: "reliability",
        key: "reliability",
        label: "Reliability",
        format: "list",
        allowed: arrayToListObjectCamelCase(["Extremely", "Very", "Moderately", "Not very", "Poor"]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      },
      sourceType: {
        enabled: true,
        canDelete: true,
        id: "sourceType",
        key: "sourceType",
        label: "Source Type",
        format: "list",
        allowed: arrayToListObjectCamelCase([
          "Published document",
          "Unpublished document",
          "Visual document",
          "Relic",
          "Artefact",
          "Secondary source",
          "Tertiary source"
        ]),
        multiple: false,
        autoCalculate: false,
        autoCalculateType: null,
        indicateItemCompletion: false
      }
    },
    allIds: ["notes", "title", "shortName", "gender", "occupation", "reliability", "sourceType"]
  },
  references: {
    byId: {
      architectRef: {
        enabled: true,
        canDelete: true,
        id: "architectRef",
        key: null,
        label: "Architect",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "deepOrange500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Architected",
        showInverse: true,
        showInInspector: true
      },
      participantRef: {
        enabled: true,
        canDelete: true,
        id: "participantRef",
        key: "participantRef",
        label: "Participant",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightPink",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      fianceRef: {
        enabled: true,
        canDelete: true,
        id: "fianceRef",
        key: "fianceRef",
        label: "Fiancé",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "fuschia",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      composerRef: {
        enabled: true,
        canDelete: true,
        id: "composerRef",
        key: null,
        label: "Composer",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "teal400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Composed",
        showInverse: true,
        showInInspector: true
      },
      endLocationRef: {
        enabled: true,
        canDelete: true,
        id: "endLocationRef",
        key: "endLocationRef",
        label: "End Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "purple500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      sculptorRef: {
        enabled: true,
        canDelete: true,
        id: "sculptorRef",
        key: null,
        label: "Sculptor",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "blue500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Sculpted",
        showInverse: true,
        showInInspector: true
      },
      rivalRef: {
        enabled: true,
        canDelete: true,
        id: "rivalRef",
        key: "rivalRef",
        label: "Rival",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "red700A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      causesRef: {
        enabled: true,
        canDelete: true,
        id: "causesRef",
        key: "causesRef",
        label: "Causes",
        types: [itemTypeIds.EVENT_ITEM_TYPE_ID, "invention", "discovery"],
        icon: "circle",
        color: "fuschia",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Caused by",
        showInverse: true,
        showInInspector: false
      },
      parentRef: {
        enabled: true,
        canDelete: true,
        id: "parentRef",
        key: "parentRef",
        label: "Parent",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "apple",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Child",
        showInverse: true,
        showInInspector: false
      },
      partnerRef: {
        enabled: true,
        canDelete: true,
        id: "partnerRef",
        key: "partnerRef",
        label: "Partner",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lightPurple",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      birthplaceRef: {
        enabled: true,
        canDelete: true,
        id: "birthplaceRef",
        key: "birthplaceRef",
        label: "Birthplace",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "apple",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      familyRef: {
        enabled: true,
        canDelete: true,
        id: "familyRef",
        key: "familyRef",
        label: "Family",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "purple200A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      authorRef: {
        enabled: true,
        canDelete: true,
        id: "authorRef",
        key: null,
        label: "Author",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "lime500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Authored",
        showInverse: true,
        showInInspector: true
      },
      siblingRef: {
        enabled: true,
        canDelete: true,
        id: "siblingRef",
        key: "siblingRef",
        label: "Sibling",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "indigo700A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      movementRef: {
        enabled: true,
        canDelete: true,
        id: "movementRef",
        key: null,
        label: "Movement",
        types: ["movement"],
        icon: "circle",
        color: "deepOrange300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      associateRef: {
        enabled: true,
        canDelete: true,
        id: "associateRef",
        key: "associateRef",
        label: "Creator",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "midBlue",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Created By",
        showInverse: true,
        showInInspector: false
      },
      friendRef: {
        enabled: true,
        canDelete: true,
        id: "friendRef",
        key: "friendRef",
        label: "Friend",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "blue300",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      categoryRef: {
        enabled: true,
        canDelete: true,
        id: "categoryRef",
        key: "categoryRef",
        label: "Category",
        types: ["category"],
        icon: "circle filled",
        color: "peach",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      painterRef: {
        enabled: true,
        canDelete: true,
        id: "painterRef",
        key: null,
        label: "Painter",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "pureYellow",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Painted",
        showInverse: true,
        showInInspector: true
      },
      sourceRef: {
        enabled: true,
        canDelete: true,
        id: "sourceRef",
        key: "sourceRef",
        label: "Source",
        types: ["source"],
        icon: "circle",
        color: "lightTeal",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      instigatorRef: {
        enabled: true,
        canDelete: true,
        id: "instigatorRef",
        key: "instigatorRef",
        label: "Instigates",
        types: [
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          "invention",
          "discovery",
          "artwork",
          "literature",
          "composition",
          "movement"
        ],
        icon: "circle",
        color: "yellow",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Instigated by",
        showInverse: true,
        showInInspector: false
      },
      seeAlsoRef: {
        enabled: true,
        canDelete: true,
        id: "seeAlsoRef",
        key: "seeAlsoRef",
        label: "See also",
        types: [itemTypeIds.EVENT_ITEM_TYPE_ID, "invention", "discovery", "artwork", "literature", "composition"],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      witnessRef: {
        enabled: true,
        canDelete: true,
        id: "witnessRef",
        key: "witnessRef",
        label: "Observer",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "peach",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      startLocationRef: {
        enabled: true,
        canDelete: true,
        id: "startLocationRef",
        key: "startLocationRef",
        label: "Start Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle",
        color: "purple400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      stepParentRef: {
        enabled: true,
        canDelete: true,
        id: "stepParentRef",
        key: "stepParentRef",
        label: "Step-Parent",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "teal500",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Stepchild",
        showInverse: true,
        showInInspector: false
      },
      grandparentRef: {
        enabled: true,
        canDelete: true,
        id: "grandparentRef",
        key: "grandparentRef",
        label: "Grandparent",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "teal400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: "Grandchild",
        showInverse: true,
        showInInspector: false
      },
      locationRef: {
        enabled: true,
        canDelete: true,
        id: "locationRef",
        key: "locationRef",
        label: "Location",
        types: [itemTypeIds.LOCATION_ITEM_TYPE_ID],
        icon: "circle filled",
        color: "deepOrange400A",
        lineStyle: "lineStyle_SOLID",
        bidirectional: false,
        inverseLabel: null,
        showInverse: false,
        showInInspector: true
      },
      spouseRef: {
        enabled: true,
        canDelete: true,
        id: "spouseRef",
        key: "spouseRef",
        label: "Spouse",
        types: [itemTypeIds.PERSON_ITEM_TYPE_ID],
        icon: "circle",
        color: "pastelPink",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      },
      [REFERENCE_RELATES_TO]: {
        enabled: true,
        canDelete: false,
        id: REFERENCE_RELATES_TO,
        key: REFERENCE_RELATES_TO,
        label: "Relates to",
        types: [
          itemTypeIds.MINDMAP_NODE_TYPE_ID,
          itemTypeIds.EVENT_ITEM_TYPE_ID,
          itemTypeIds.PERSON_ITEM_TYPE_ID,
          itemTypeIds.LOCATION_ITEM_TYPE_ID,
          "category",
          "source",
          "invention",
          "discovery",
          itemTypeIds.NARRATIVE_FOLDER_TYPE_ID,
          "movement",
          "artwork",
          "composition",
          "literature"
        ],
        icon: "circle",
        color: "mono",
        lineStyle: "lineStyle_SOLID",
        bidirectional: true,
        inverseLabel: null,
        showInverse: false,
        showInInspector: false
      }
    },
    allIds: [
      "participantRef",
      "witnessRef",
      "causesRef",
      "instigatorRef",
      "seeAlsoRef",
      "locationRef",
      "startLocationRef",
      "endLocationRef",
      "birthplaceRef",
      "categoryRef",
      "sourceRef",
      "associateRef",
      "familyRef",
      "friendRef",
      "rivalRef",
      "parentRef",
      "grandparentRef",
      "stepParentRef",
      "siblingRef",
      "spouseRef",
      "fianceRef",
      "partnerRef",
      "sculptorRef",
      "composerRef",
      "painterRef",
      "architectRef",
      "authorRef",
      "movementRef",
      REFERENCE_RELATES_TO
    ]
  },
  calendar,
  colors: {
    byId: {
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC": {
        id: "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
        label: "Light Purple",
        swatchColor: "lightPurple"
      },
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5": {
        id: "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
        label: "Light Teal",
        swatchColor: "lightTeal"
      },
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4": {
        id: "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
        label: "Denim",
        swatchColor: "denim"
      },
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187": {
        id: "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
        label: "Yellow",
        swatchColor: "yellow"
      },
      "199ABF77-578A-4EA8-8C2D-8EA080610A78": {
        id: "199ABF77-578A-4EA8-8C2D-8EA080610A78",
        label: "Dark Purple",
        swatchColor: "darkPurple"
      },
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49": {
        id: "D9DAECFF-F3CE-4399-9690-5E045E8BFA49",
        label: "Light Blue",
        swatchColor: "lightBlue"
      },
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7": {
        id: "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
        label: "Apple",
        swatchColor: "apple"
      },
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7": {
        id: "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
        label: "Red",
        swatchColor: "red"
      },
      "7F4245A2-AF3B-4633-BEBA-68494486C574": {
        id: "7F4245A2-AF3B-4633-BEBA-68494486C574",
        label: "Peach",
        swatchColor: "peach"
      }
    },
    allIds: [
      "0CAFEDAF-A92E-4012-9E43-438B826F5FC7",
      "0F5068F1-B5BA-45B2-9D55-6C3C8BD482AC",
      "199ABF77-578A-4EA8-8C2D-8EA080610A78",
      "2AF2A95C-00F9-4A92-BE05-CDD5641D28D5",
      "7F4245A2-AF3B-4633-BEBA-68494486C574",
      "88F01362-D7BA-4AD9-8C93-E0EC45A604B4",
      "B6D4A6F6-57EA-4E32-8242-5008B57A8AD7",
      "D6DB1C49-D913-4D7C-884B-1F7AF1F53187",
      "D9DAECFF-F3CE-4399-9690-5E045E8BFA49"
    ]
  },
  timelineViewSettings: {
    scrollBounds: "infinite",
    eventLayout: "clearAll",
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    showDependencies: true,
    zoomFrom: TimelineZoomLimits.Thousand,
    zoomTo: TimelineZoomLimits.Year,
    showTypes: [itemTypeIds.EVENT_ITEM_TYPE_ID, "artwork", "composition", "literature"]
  },
  narrativeViewSettings: {
    showIdentifier: false,
    showDates: false,
    showDuration: false,
    showNarrative: true,
    showRelatedAvatars: true,
    showImage: false,
    showSummary: false,
    showInfoIcons: false,
    columnLevel: 0
  },
  narrativeSettings: {
    labels: defaultGenericNarrativeLabels,
    autoDeleteWhenRemoveContainer: true,
    numbering: narrativeNumberingOptions.CONTINUOUS_STYLE
  },
  mindmapViewSettings: {
    showIdentifier: true,
    showDates: false,
    showDuration: false,
    showNarrative: false,
    showRelationshipTypes: [],
    showImage: false,
    showSummary: true,
    showInfoIcons: false
  },
  subwayViewSettings: {
    showDates: false,
    display: subwayDisplayOptions.SUBWAY
  },
  relationshipViewSettings: {
    showIdentifier: false,
    showDates: true,
    showDuration: false,
    showNarrative: false,
    showSummary: false,
    showInfoIcons: false
  },
  moveChildItemsWithParent: false
};
